/**
 * Situation map (bottom-right): Canvas2D overview of terrain, believed ownership, fog, cities,
 * own units and enemy contacts, plus the renderer's viewport (bus 'camera').
 * Click or drag to pan the main map (bus 'panTo').
 */
import { type GameMap, HEX_SIZE, type HexGrid, NO_FACTION, type PlayerView, Terrain } from '@friction/engine';
import { type PointerEvent, useEffect, useMemo, useRef } from 'react';
import { type BusEvents, bus } from '../../game/bus';
import { cssColor, lighten } from '../../game/format';
import { gameStore, useGame } from '../../game/store';
import { useT } from '../../i18n';
import { theatreTitle } from '../../i18n/names';
import { cx } from '../common/controls';
import hud from './hud.module.css';

const MAX_W = 264;
const MAX_H = 180;

const TERRAIN_FILL: Record<number, string> = {
  [Terrain.Ocean]: '#5f8394',
  [Terrain.Shallows]: '#8fb0b8',
  [Terrain.Lake]: '#7c9fae',
  [Terrain.Plains]: '#d8cfa9',
  [Terrain.Forest]: '#a3ad84',
  [Terrain.Hills]: '#c9bb92',
  [Terrain.Mountains]: '#b3a283',
  [Terrain.Marsh]: '#b5bb96',
  [Terrain.Urban]: '#c4b393',
};

function hexPath(ctx: CanvasRenderingContext2D, grid: HexGrid, h: number, s: number, r: number): void {
  const cx = grid.cx(h) * s;
  const cy = grid.cy(h) * s;
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  }
  ctx.closePath();
}

function paintTerrain(map: GameMap, grid: HexGrid, w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.round(w * dpr);
  c.height = Math.round(h * dpr);
  const ctx = c.getContext('2d')!;
  ctx.scale(dpr, dpr);
  ctx.fillStyle = TERRAIN_FILL[Terrain.Ocean];
  ctx.fillRect(0, 0, w, h);
  const s = w / grid.worldWidth;
  const r = HEX_SIZE * s * 1.06; // slight overlap hides seams
  const byTerrain = new Map<number, number[]>();
  for (let i = 0; i < grid.count; i++) {
    const t = map.terrain[i];
    const list = byTerrain.get(t);
    if (list) list.push(i);
    else byTerrain.set(t, [i]);
  }
  for (const [t, list] of byTerrain) {
    ctx.beginPath();
    for (const i of list) hexPath(ctx, grid, i, s, r);
    ctx.fillStyle = TERRAIN_FILL[t] ?? '#d8cfa9';
    ctx.fill();
  }
  // Rivers as thin blue lines.
  ctx.strokeStyle = 'rgba(62, 104, 128, 0.9)';
  ctx.lineWidth = 0.9;
  for (const rv of map.rivers) {
    ctx.beginPath();
    for (let k = 0; k < rv.points.length; k += 2) {
      const x = rv.points[k] * s;
      const y = rv.points[k + 1] * s;
      if (k) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    }
    ctx.stroke();
  }
  return c;
}

function paintOverlay(
  ctx: CanvasRenderingContext2D,
  base: HTMLCanvasElement,
  view: PlayerView,
  map: GameMap,
  grid: HexGrid,
  w: number,
  h: number,
  cam: BusEvents['camera'] | null,
): void {
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(base, 0, 0, w, h);
  const s = w / grid.worldWidth;
  const r = HEX_SIZE * s * 1.06;

  // Believed ownership, one path per faction.
  for (const f of view.factions) {
    ctx.beginPath();
    let any = false;
    for (let i = 0; i < grid.count; i++) {
      if (view.owner[i] === f.id && view.owner[i] !== NO_FACTION && map.terrain[i] >= Terrain.Plains) {
        hexPath(ctx, grid, i, s, r);
        any = true;
      }
    }
    if (!any) continue;
    ctx.globalAlpha = f.id === view.faction ? 0.42 : 0.38;
    ctx.fillStyle = cssColor(f.color);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Fog: land outside current observation is dimmed.
  ctx.beginPath();
  for (let i = 0; i < grid.count; i++)
    if (!view.visible[i] && map.terrain[i] >= Terrain.Plains) hexPath(ctx, grid, i, s, r);
  ctx.fillStyle = 'rgba(16, 18, 22, 0.42)';
  ctx.fill();

  // Cities
  for (const c of map.cities) {
    const x = grid.cx(c.hex) * s;
    const y = grid.cy(c.hex) * s;
    const size = c.size === 3 ? 3.4 : c.size === 2 ? 2.6 : 1.8;
    ctx.fillStyle = '#2b2620';
    ctx.fillRect(x - size / 2, y - size / 2, size, size);
    if (c.capitalOf !== NO_FACTION) {
      ctx.strokeStyle = '#2b2620';
      ctx.lineWidth = 0.9;
      ctx.strokeRect(x - size / 2 - 1.6, y - size / 2 - 1.6, size + 3.2, size + 3.2);
    }
  }

  // Battles
  for (const b of view.battles) {
    const x = grid.cx(b.hex) * s;
    const y = grid.cy(b.hex) * s;
    ctx.strokeStyle = '#e0634e';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(x - 3, y - 3);
    ctx.lineTo(x + 3, y + 3);
    ctx.moveTo(x + 3, y - 3);
    ctx.lineTo(x - 3, y + 3);
    ctx.stroke();
  }

  // Enemy contacts
  for (const c of view.contacts) {
    const f = view.factions.find((x) => x.id === c.faction);
    const x = grid.cx(c.hex) * s;
    const y = grid.cy(c.hex) * s;
    ctx.beginPath();
    ctx.arc(x, y, 2.1, 0, Math.PI * 2);
    if (c.visibleNow) {
      ctx.fillStyle = cssColor(f?.color ?? 0x777777);
      ctx.fill();
      ctx.strokeStyle = '#140f0c';
      ctx.lineWidth = 0.8;
      ctx.stroke();
    } else {
      ctx.strokeStyle = cssColor(f?.color ?? 0x777777);
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.75;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  // Own and allied units
  const sel = new Set(gameStore.getState().selection);
  for (const u of view.units) {
    const x = grid.cx(u.hex) * s;
    const y = grid.cy(u.hex) * s;
    const own = u.faction === view.faction;
    const f = own ? view.me.color : (view.factions.find((q) => q.id === u.faction)?.color ?? 0x999999);
    ctx.fillStyle = cssColor(lighten(f, own ? 0.15 : 0.35));
    ctx.strokeStyle = sel.has(u.id) ? '#f3d58f' : '#140f0c';
    ctx.lineWidth = sel.has(u.id) ? 1.4 : 0.8;
    ctx.fillRect(x - 2.3, y - 1.7, 4.6, 3.4);
    ctx.strokeRect(x - 2.3, y - 1.7, 4.6, 3.4);
  }

  // Viewport
  if (cam) {
    ctx.strokeStyle = '#e8cf95';
    ctx.lineWidth = 1.2;
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 3;
    ctx.strokeRect(cam.x * s, cam.y * s, cam.width * s, cam.height * s);
    ctx.shadowBlur = 0;
  }
}

export function Minimap() {
  const t = useT();
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  const selection = useGame((g) => g.selection);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cam = useRef<BusEvents['camera'] | null>(null);
  const redraw = useRef<() => void>(() => {});

  const size = useMemo(() => {
    if (!grid) return { w: MAX_W, h: MAX_H };
    const aspect = grid.worldHeight / grid.worldWidth;
    let w = MAX_W;
    let h = w * aspect;
    if (h > MAX_H) {
      h = MAX_H;
      w = h / aspect;
    }
    return { w: Math.round(w), h: Math.round(h) };
  }, [grid]);

  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const base = useMemo(
    () => (map && grid ? paintTerrain(map, grid, size.w, size.h, dpr) : null),
    [map, grid, size, dpr],
  );

  useEffect(() => {
    let raf = 0;
    redraw.current = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const c = canvasRef.current;
        const { view: v, map: m, grid: g } = gameStore.getState();
        if (!c || !v || !m || !g || !base) return;
        const ctx = c.getContext('2d')!;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        paintOverlay(ctx, base, v, m, g, size.w, size.h, cam.current);
      });
    };
    const off = bus.on('camera', (c) => {
      cam.current = c;
      redraw.current();
    });
    return () => {
      off();
      cancelAnimationFrame(raf);
    };
  }, [base, size, dpr]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: repaint whenever the view or selection changes
  useEffect(() => redraw.current(), [view, selection, base]);

  if (!map || !grid) return null;

  const pan = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * grid.worldWidth;
    const y = ((e.clientY - rect.top) / rect.height) * grid.worldHeight;
    bus.emit('panTo', { x, y });
  };

  return (
    <section className={cx(hud.plate, hud.minimap)} aria-label={t('minimap.title')} data-tut="minimap">
      <div className={hud.minimapHead}>
        <span>{t('minimap.title')}</span>
        <span className={hud.minimapName}>{theatreTitle(map.name, t.locale)}</span>
      </div>
      <div className={hud.minimapFrame} style={{ width: size.w, height: size.h }}>
        <canvas
          ref={canvasRef}
          width={Math.round(size.w * dpr)}
          height={Math.round(size.h * dpr)}
          style={{ width: size.w, height: size.h }}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            pan(e);
          }}
          onPointerMove={(e) => {
            if (e.buttons & 1) pan(e);
          }}
        />
      </div>
    </section>
  );
}
